<script setup lang="ts">
import { useElementVisibility } from "@vueuse/core";
import type { ClassValue } from "clsx";
import type { StyleValue } from "vue";
import { cn } from "~/utils";

defineProps<{
  src: string;
  class?: ClassValue;
  style?: StyleValue;
  width?: number | null;
  height?: number | null;
  lazy?: boolean;
  alt?: string;
}>();

const imgRef = useTemplateRef("image");
const imgLoaded = ref(false);
const imgVisible = useElementVisibility(imgRef, {
  once: true,
  initialValue: true,
});

const shouldBeShown = computed((oldValue) => {
  if (oldValue === true) return true;
  return imgLoaded.value && imgVisible.value;
});

onMounted(() => {
  if (imgRef.value && (imgRef.value.complete || imgRef.value.naturalWidth > 0)) {
    imgLoaded.value = true;
  }
});
</script>

<template>
  <img
    :class="
      cn(
        'transition duration-300 ease-out border-none outline-none',
        shouldBeShown ? 'scale-100 opacity-100' : 'scale-96 opacity-0',
        $props.class,
      )
    "
    ref="image"
    :loading="$props.lazy !== false ? 'lazy' : 'eager'"
    :src="$props.src"
    :width="$props.width"
    :height="$props.height"
    :alt="$props.alt ?? ''"
    @load="imgLoaded = true"
    @error="imgLoaded = true"
    :style="[
      $props.style,
      {
        WebkitTapHighlightColor: 'transparent',
        WebkitBackfaceVisibility: 'hidden',
        backfaceVisibility: 'hidden',
        transform: 'translateZ(0)',
        boxShadow: 'none',
        color: 'transparent',
        WebkitAppearance: 'none',
        backgroundColor: 'transparent',
        WebkitClipPath: 'inset(1px)',
        clipPath: 'inset(1px)',
      }
    ]"
  />
</template>
